
import { 
    doc, getDoc, setDoc, collection, getDocs, writeBatch, query, 
    deleteDoc, updateDoc, limit, orderBy, where, serverTimestamp, 
    addDoc, Timestamp, getCountFromServer, getDocFromServer, increment,
    onSnapshot
} from 'firebase/firestore';
import { db, auth } from '../firebaseConfig';
import { sendPasswordResetEmail } from 'firebase/auth';
import { del } from 'idb-keyval';
import { cacheData, getCachedData, invalidateCache } from '../utils/cache';

export enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

interface FirestoreErrorInfo {
  error: string;
  operationType: OperationType;
  path: string | null;
  authInfo: {
    userId?: string;
    email?: string | null;
    emailVerified?: boolean;
    isAnonymous?: boolean;
    tenantId?: string | null;
    providerInfo: {
      providerId: string;
      displayName: string | null;
      email: string | null;
      photoUrl: string | null;
    }[];
  }
}

export function handleFirestoreError(error: unknown, operationType: OperationType, path: string | null) {
  const errInfo: FirestoreErrorInfo = {
    error: error instanceof Error ? error.message : String(error),
    authInfo: {
      userId: auth.currentUser?.uid,
      email: auth.currentUser?.email,
      emailVerified: auth.currentUser?.emailVerified,
      isAnonymous: auth.currentUser?.isAnonymous,
      tenantId: auth.currentUser?.tenantId,
      providerInfo: auth.currentUser?.providerData.map(provider => ({
        providerId: provider.providerId,
        displayName: provider.displayName,
        email: provider.email,
        photoUrl: provider.photoURL
      })) || []
    },
    operationType,
    path
  }
  console.error('Firestore Error: ', JSON.stringify(errInfo));
  throw new Error(JSON.stringify(errInfo));
}

/**
 * Helper to perform batch writes in chunks of 500 to avoid Firestore limits.
 */
async function chunkedBatchWrite(
    items: any[], 
    operation: (batch: any, item: any) => void,
    onComplete?: () => Promise<void>
) {
    const CHUNK_SIZE = 500;
    for (let i = 0; i < items.length; i += CHUNK_SIZE) {
        const chunk = items.slice(i, i + CHUNK_SIZE);
        const batch = writeBatch(db);
        chunk.forEach(item => operation(batch, item));
        await batch.commit();
    }
    if (onComplete) await onComplete();
}

import type { 
    InvoiceSettings, Unit, Owner, Vehicle, WaterReading, 
    ChargeRaw, Adjustment, UserPermission, ActivityLog, 
    AllData, PaymentStatus, MonthlyStat, SystemMetadata, 
    ProfileRequest, TariffCollection, AdminNotification,
    Role, NewsItem, MiscRevenue, OperationalExpense,
    RegistrationStatus, FeedbackItem, FeedbackReply, ServiceRegistration,
    OtherFeeConfig, VisitStat
} from '../types';

// Tăng tốc độ đọc bằng cách sử dụng bộ nhớ đệm nội bộ và IndexedDB
let settingsCache: { invoice?: InvoiceSettings, tariffs?: TariffCollection, metadata?: SystemMetadata } = {};

export const incrementVersion = async (key: keyof SystemMetadata) => {
    const ref = doc(db, 'settings', 'metadata');
    try {
        const snap = await getDoc(ref);
        const current = snap.exists() ? snap.data() as SystemMetadata : { units_version: 0, owners_version: 0, vehicles_version: 0, tariffs_version: 0, users_version: 0 };
        const newVal = (current[key] || 0) + 1;
        await setDoc(ref, { [key]: newVal }, { merge: true });
        // Update local cache
        if (settingsCache.metadata) {
            settingsCache.metadata[key] = newVal as any;
        }
    } catch (e) {
        console.warn("Failed to increment version for", key);
    }
};

export const testConnection = async () => {
  try {
    await getDocFromServer(doc(db, 'settings', 'metadata'));
  } catch (error) {
    if(error instanceof Error && error.message.includes('the client is offline')) {
      console.error("Please check your Firebase configuration. ");
    }
  }
}
testConnection();

let metadataPromise: Promise<SystemMetadata> | null = null;

export const getSystemMetadata = async (): Promise<SystemMetadata> => {
    if (metadataPromise) return metadataPromise;

    metadataPromise = (async () => {
        try {
            const snap = await getDoc(doc(db, 'settings', 'metadata'));
            if (snap.exists()) {
                return snap.data() as SystemMetadata;
            }
        } catch (e) {
            console.warn("Metadata not found or denied");
        }
        return { units_version: 0, owners_version: 0, vehicles_version: 0, tariffs_version: 0, users_version: 0 };
    })();

    // Clear the promise after a short delay so subsequent calls fetch fresh data
    setTimeout(() => {
        metadataPromise = null;
    }, 2000);

    return metadataPromise;
};

export const fetchCollection = async <T>(colName: string, limitCount: number = 1500): Promise<T[]> => {
    try {
        const q = query(collection(db, colName), limit(limitCount));
        const snap = await getDocs(q);
        return snap.docs.map(d => ({ ...d.data(), id: d.id } as any));
    } catch (e) {
        console.error(`Error fetching collection ${colName}:`, e);
        return [];
    }
};

export const subscribeToCollection = <T>(colName: string, callback: (data: T[]) => void, limitCount: number = 100) => {
    const q = query(collection(db, colName), orderBy('updatedAt', 'desc'), limit(limitCount));
    return onSnapshot(q, (snap) => {
        const data = snap.docs.map(d => ({ ...d.data(), id: d.id } as any));
        callback(data);
    }, (e) => {
        console.error(`Error subscribing to ${colName}:`, e);
    });
};

export const fetchCollectionWithOnSnapshot = fetchCollection;

export const loadAllData = async () => {
    // This is a heavy operation, we use smart caching to minimize reads
    const [
        units, owners, vehicles, tariffs, invoiceSettings, waterLocks
    ] = await Promise.all([
        fetchCollectionWithOnSnapshot<Unit>('units'),
        fetchCollectionWithOnSnapshot<Owner>('owners'),
        fetchCollectionWithOnSnapshot<Vehicle>('vehicles'),
        fetchTariffsData(),
        fetchInvoiceSettings(),
        fetchWaterLocks()
    ]);

    return {
        units, owners, vehicles, tariffs, invoiceSettings,
        lockedWaterPeriods: waterLocks,
        hasData: units.length > 0
    };
};

export const getDashboardCounts = async () => {
    // Use getCountFromServer to save reads (1 read per 1000 docs)
    try {
        const [unitsCount, vehiclesCount, ownersCount] = await Promise.all([
            getCountFromServer(collection(db, 'units')),
            getCountFromServer(query(collection(db, 'vehicles'), where('isActive', '==', true))),
            getCountFromServer(collection(db, 'owners'))
        ]);
        
        return {
            totalUnits: unitsCount.data().count,
            activeVehicles: vehiclesCount.data().count,
            totalOwners: ownersCount.data().count
        };
    } catch (e) {
        console.warn("Failed to fetch counts from server, falling back to 0");
        return { totalUnits: 0, activeVehicles: 0, totalOwners: 0 };
    }
};

export const fetchInvoiceSettings = async (): Promise<InvoiceSettings | null> => {
    if (settingsCache.invoice) return settingsCache.invoice;
    
    // Try IndexedDB cache first
    const cached = await getCachedData<InvoiceSettings>('settings_invoice');
    if (cached) {
        settingsCache.invoice = cached;
        return cached;
    }

    const path = 'settings/invoice';
    try {
        const snap = await getDoc(doc(db, 'settings', 'invoice'));
        if (snap.exists()) {
            const data = snap.data() as InvoiceSettings;
            settingsCache.invoice = data;
            await cacheData('settings_invoice', data, 60); // Cache for 1 hour
            return data;
        }
    } catch (e) {
        if (e instanceof Error && e.message.includes('permission-denied')) {
            handleFirestoreError(e, OperationType.GET, path);
        }
        console.warn("Permission denied for invoice settings");
    }
    return null;
};

export const fetchTariffsData = async (): Promise<TariffCollection> => {
    if (settingsCache.tariffs) return settingsCache.tariffs;
    
    const metadata = await getSystemMetadata();
    const currentVersion = metadata.tariffs_version || 0;
    const cacheKey = 'settings_tariffs';
    
    const cached = await getCachedData<{ data: TariffCollection, version: number }>(cacheKey);
    if (cached && cached.version === currentVersion) {
        settingsCache.tariffs = cached.data;
        return cached.data;
    }

    const path = 'settings/tariffs';
    try {
        const snap = await getDoc(doc(db, 'settings', 'tariffs'));
        const data = snap.exists() ? snap.data() as TariffCollection : { service: [], parking: [], water: [] };
        settingsCache.tariffs = data;
        await cacheData(cacheKey, { data, version: currentVersion }, 1440);
        return data;
    } catch (e) {
        if (e instanceof Error && e.message.includes('permission-denied')) {
            handleFirestoreError(e, OperationType.GET, path);
        }
        console.warn("Permission denied for tariffs");
        return { service: [], parking: [], water: [] };
    }
};

export const fetchActiveCharges = async (periods: string[], force: boolean = false): Promise<ChargeRaw[]> => {
    if (periods.length === 0) return [];
    
    // Smart period-based caching
    const results: ChargeRaw[] = [];
    const periodsToFetch: string[] = [];
    
    for (const p of periods) {
        const cacheKey = `charges_${p}`;
        if (force) {
            await invalidateCache(cacheKey);
            periodsToFetch.push(p);
        } else {
            const cached = await getCachedData<ChargeRaw[]>(cacheKey);
            if (cached) {
                results.push(...cached);
            } else {
                periodsToFetch.push(p);
            }
        }
    }
    
    if (periodsToFetch.length === 0) return results;

    const path = 'charges';
    try {
        const chargesRef = collection(db, 'charges');
        const q = query(
            chargesRef, 
            where('Period', 'in', periodsToFetch),
            limit(1000) 
        );
        const snap = await getDocs(q);
        const fetched = snap.docs.map(d => d.data() as ChargeRaw);
        
        // Group and cache by period
        for (const p of periodsToFetch) {
            const periodData = fetched.filter(c => c.Period === p);
            if (periodData.length > 0) {
                await cacheData(`charges_${p}`, periodData, 30); // Cache for 30 mins
            }
        }
        
        return [...results, ...fetched];
    } catch (e) {
        if (e instanceof Error && e.message.includes('permission-denied')) {
            handleFirestoreError(e, OperationType.LIST, path);
        }
        console.warn("Failed to fetch active charges:", e);
        return results;
    }
};

export const fetchChargesForResident = async (residentId: string): Promise<ChargeRaw[]> => {
    try {
        const q = query(collection(db, 'charges'), where('UnitID', '==', residentId), orderBy('Period', 'desc'), limit(12));
        const snap = await getDocs(q);
        return snap.docs.map(d => d.data() as ChargeRaw);
    } catch (e) {
        console.warn("Failed to fetch resident charges");
        return [];
    }
};

export const fetchResidentSpecificData = async (residentId: string) => {
    try {
        const unitsRef = collection(db, 'units');
        const q = query(unitsRef, where('UnitID', '==', residentId), limit(1));
        const unitSnap = await getDocs(q);
        
        if (unitSnap.empty) return { unit: null, owner: null, vehicles: [] };
        
        const unitData = unitSnap.docs[0].data() as Unit;
        
        const ownerRef = doc(db, 'owners', unitData.OwnerID);
        const ownerSnap = await getDoc(ownerRef);
        const ownerData = ownerSnap.exists() ? ownerSnap.data() as Owner : null;
        
        const vehiclesRef = collection(db, 'vehicles');
        const vQuery = query(vehiclesRef, where('UnitID', '==', residentId), where('isActive', '==', true));
        const vSnap = await getDocs(vQuery);
        const unitVehicles = vSnap.docs.map(d => d.data() as Vehicle);
        
        return { unit: unitData, owner: ownerData, vehicles: unitVehicles };
    } catch (e) {
        console.warn("Error fetching resident specific data");
        return { unit: null, owner: null, vehicles: [] };
    }
};

// --- HELPER FUNCTIONS ---

export const injectLogAndNotif = (
    batch: any, // Using any to avoid complex WriteBatch typing issues in this context
    log: {
        actionType: 'CREATE' | 'UPDATE' | 'DELETE' | 'WIPE' | 'RESTORE',
        module: string,
        description: string,
        performedBy: { name: string, email: string },
        ids?: string[],
        linkTo?: string
    }
) => {
    const logRef = doc(collection(db, 'activity_logs'));
    batch.set(logRef, {
        ...log,
        timestamp: serverTimestamp()
    });

    const notifRef = doc(collection(db, 'admin_notifications'));
    batch.set(notifRef, {
        type: 'request',
        title: `${log.module}: ${log.actionType}`,
        message: log.description,
        isRead: false,
        createdAt: serverTimestamp(),
        linkTo: log.linkTo || '' // Fix: Ensure linkTo is never undefined
    });
};

export const fetchLatestLogs = async (limitCount: number = 20): Promise<ActivityLog[]> => {
    try {
        const q = query(collection(db, 'activity_logs'), orderBy('timestamp', 'desc'), limit(limitCount));
        const snap = await getDocs(q);
        return snap.docs.map(d => {
            const data = d.data();
            return { 
                id: d.id, 
                ts: data.timestamp instanceof Timestamp ? data.timestamp.toDate().toISOString() : new Date().toISOString(), 
                actor_email: data.performedBy?.email || 'system', 
                summary: data.description || '', 
                module: data.module || 'System', 
                action: data.actionType || 'UPDATE' 
            } as any;
        });
    } catch (e) {
        return [];
    }
};

export const fetchNews = async (): Promise<NewsItem[]> => {
    const metadata = await getSystemMetadata();
    const currentVersion = metadata.news_version || 0;
    const cacheKey = 'news_active';
    
    const cached = await getCachedData<{ data: NewsItem[], version: number }>(cacheKey);
    if (cached && cached.version === currentVersion) {
        return cached.data;
    }

    try {
        const q = query(collection(db, 'news'), where('isArchived', '==', false), orderBy('date', 'desc'), limit(15));
        const snap = await getDocs(q);
        const data = snap.docs.map(d => ({ id: d.id, ...d.data() } as NewsItem));
        await cacheData(cacheKey, { data, version: currentVersion }, 60);
        return data;
    } catch (e) {
        return [];
    }
};

export const fetchRecentWaterReadings = async (periods: string[], force: boolean = false): Promise<WaterReading[]> => {
    if (periods.length === 0) return [];
    
    const results: WaterReading[] = [];
    const periodsToFetch: string[] = [];
    
    for (const p of periods) {
        const cacheKey = `water_${p}`;
        if (force) {
            await invalidateCache(cacheKey);
            periodsToFetch.push(p);
        } else {
            const cached = await getCachedData<WaterReading[]>(cacheKey);
            if (cached) {
                results.push(...cached);
            } else {
                periodsToFetch.push(p);
            }
        }
    }
    
    if (periodsToFetch.length === 0) return results;

    try {
        const q = query(collection(db, 'waterReadings'), where('Period', 'in', periodsToFetch), limit(5000));
        const snap = await getDocs(q);
        const fetched = snap.docs.map(d => d.data() as WaterReading);
        
        for (const p of periodsToFetch) {
            const periodData = fetched.filter(w => w.Period === p);
            if (periodData.length > 0) {
                await cacheData(`water_${p}`, periodData, 30);
            }
        }
        
        return [...results, ...fetched];
    } catch (e) {
        return results;
    }
};

export const fetchWaterLocks = async (): Promise<string[]> => {
    try {
        const snap = await getDoc(doc(db, 'settings', 'water_locks'));
        if (snap.exists()) return snap.data().periods || [];
    } catch (e) {}
    return [];
};

export const fetchRecentAdjustments = async (startPeriod: string): Promise<Adjustment[]> => {
    try {
        const q = query(collection(db, 'adjustments'), where('Period', '>=', startPeriod), limit(500));
        const snap = await getDocs(q);
        return snap.docs.map(d => d.data() as Adjustment);
    } catch (e) {
        return [];
    }
};

export const fetchCollectionOld = async <T>(colName: string): Promise<T[]> => {
    try {
        const snap = await getDocs(query(collection(db, colName), limit(1500)));
        return snap.docs.map(d => d.data() as T);
    } catch (e) {
        return [];
    }
};

export const updateFeeSettings = async (settings: InvoiceSettings) => {
    settingsCache.invoice = settings;
    await setDoc(doc(db, 'settings', 'invoice'), settings, { merge: true });
    await invalidateCache('settings_invoice');
};

export const setLockStatus = async (period: string, isLocked: boolean) => {
    const ref = doc(db, 'settings', 'water_locks');
    const snap = await getDoc(ref);
    let periods: string[] = [];
    if (snap.exists()) {
        periods = snap.data().periods || [];
    }
    if (isLocked) {
        if (!periods.includes(period)) periods.push(period);
    } else {
        periods = periods.filter(p => p !== period);
    }
    return setDoc(ref, { periods });
};

export const getBillingLockStatus = async (period: string): Promise<boolean> => {
    try {
        const snap = await getDoc(doc(db, 'billing_locks', period));
        return snap.exists() ? snap.data().isLocked : false;
    } catch (e) {
        return false;
    }
};

export const setBillingLockStatus = async (period: string, isLocked: boolean) => {
    return setDoc(doc(db, 'billing_locks', period), { isLocked, updatedAt: serverTimestamp() });
};

export const saveNewsItem = async (item: NewsItem): Promise<string> => {
    let id = item.id;
    if (item.id && !item.id.includes('news_mock')) {
        await setDoc(doc(db, 'news', item.id), item, { merge: true });
    } else {
        const ref = await addDoc(collection(db, 'news'), { ...item, id: '' });
        await updateDoc(ref, { id: ref.id });
        id = ref.id;
    }
    await incrementVersion('news_version');
    return id;
};

export const updateUnitGrouping = async (mainUnitId: string, subUnitIds: string[], actor: { email: string, role: Role }) => {
    const batch = writeBatch(db);
    
    // Set ParentUnitID for sub-units
    subUnitIds.forEach(id => {
        batch.update(doc(db, 'units', id), { ParentUnitID: mainUnitId, updatedAt: new Date().toISOString() });
    });
    
    // Clear ParentUnitID for main unit (just in case it was a sub-unit before)
    batch.update(doc(db, 'units', mainUnitId), { ParentUnitID: null, updatedAt: new Date().toISOString() });

    injectLogAndNotif(batch, {
        actionType: 'UPDATE',
        module: 'Units',
        description: `Ghép ${subUnitIds.length} căn hộ vào căn chính ${mainUnitId}`,
        performedBy: { name: actor.role, email: actor.email },
        ids: [mainUnitId, ...subUnitIds],
        linkTo: 'adminPortalBilling'
    });

    await batch.commit();
    await incrementVersion('units_version');
};

export const ungroupUnits = async (unitIds: string[], actor: { email: string, role: Role }) => {
    const batch = writeBatch(db);
    
    unitIds.forEach(id => {
        batch.update(doc(db, 'units', id), { ParentUnitID: null, updatedAt: new Date().toISOString() });
    });

    injectLogAndNotif(batch, {
        actionType: 'UPDATE',
        module: 'Units',
        description: `Bỏ ghép ${unitIds.length} căn hộ`,
        performedBy: { name: actor.role, email: actor.email },
        ids: unitIds,
        linkTo: 'adminPortalBilling'
    });

    await batch.commit();
    await incrementVersion('units_version');
};

export const deleteNewsItem = async (id: string) => {
    await deleteDoc(doc(db, 'news', id));
    await incrementVersion('news_version');
};

export const saveChargesBatch = async (newCharges: ChargeRaw[], periodStat?: MonthlyStat) => {
    const periodsToInvalidate = new Set<string>();
    
    try {
        await chunkedBatchWrite(newCharges, (batch, c) => {
            const id = `${c.Period}_${c.UnitID}`;
            batch.set(doc(db, 'charges', id), c);
            periodsToInvalidate.add(c.Period);
        }, async () => {
            if (periodStat) {
                await setDoc(doc(db, 'monthly_stats', periodStat.period), periodStat);
                await incrementVersion('stats_version');
            }
        });
        
        // Clear cache for affected periods
        for (const p of periodsToInvalidate) {
            await invalidateCache(`charges_${p}`);
        }
    } catch (e) {
        handleFirestoreError(e, OperationType.WRITE, 'charges');
    }
};

export const confirmSinglePayment = async (charge: ChargeRaw, finalPaidAmount: number, status: PaymentStatus = 'paid') => {
    const id = `${charge.Period}_${charge.UnitID}`;
    await updateDoc(doc(db, 'charges', id), {
        TotalPaid: finalPaidAmount,
        paymentStatus: status,
        PaymentConfirmed: true,
        updatedAt: serverTimestamp()
    });
    await invalidateCache(`charges_${charge.Period}`);
};

export const saveUsers = async (users: UserPermission[]) => {
    try {
        await chunkedBatchWrite(users, (batch, u) => {
            batch.set(doc(db, 'users', u.Email), u, { merge: true });
        });
        await incrementVersion('users_version');
    } catch (e) {
        handleFirestoreError(e, OperationType.WRITE, 'users');
    }
};

export const deleteUsers = async (emails: string[]) => {
    try {
        await chunkedBatchWrite(emails, (batch, email) => {
            batch.delete(doc(db, 'users', email));
        });
        await incrementVersion('users_version');
    } catch (e) {
        handleFirestoreError(e, OperationType.DELETE, 'users');
    }
};

export const updateUserProfile = async (email: string, updates: Partial<UserPermission>) => {
    const cleanData = JSON.parse(JSON.stringify(updates));
    return setDoc(doc(db, 'users', email), cleanData, { merge: true });
};

export const updateResidentData = async (
    _u: any, _o: any, _v: any,
    updatedData: { unit: Unit; owner: Owner; vehicles: Vehicle[] },
    actor: { email: string, role: Role },
    reason: string,
    resolution?: { requestId: string, status: 'APPROVED' | 'REJECTED' }
) => {
    const batch = writeBatch(db);
    const { unit, owner, vehicles } = updatedData;
    
    batch.set(doc(db, 'units', unit.UnitID), unit, { merge: true });
    batch.set(doc(db, 'owners', owner.OwnerID), owner, { merge: true });
    
    vehicles.forEach(v => {
        batch.set(doc(db, 'vehicles', v.VehicleId), v, { merge: true });
    });

    // Deactivate removed vehicles
    const oldVehiclesForUnit = (_v || []).filter((v: any) => v.UnitID === unit.UnitID && v.isActive);
    const removedVehicles = oldVehiclesForUnit.filter((ov: any) => !vehicles.some((nv: any) => nv.VehicleId === ov.VehicleId));
    removedVehicles.forEach((rv: any) => {
        batch.update(doc(db, 'vehicles', rv.VehicleId), { isActive: false, updatedAt: new Date().toISOString() });
    });

    if (resolution) {
        batch.update(doc(db, 'profileRequests', resolution.requestId), {
            status: resolution.status,
            resolvedAt: serverTimestamp(),
            resolvedBy: actor.email
        });
    }

    injectLogAndNotif(batch, {
        actionType: 'UPDATE',
        module: 'Residents',
        description: `Cập nhật hồ sơ căn ${unit.UnitID}. Lý do: ${reason}`,
        performedBy: { name: actor.role, email: actor.email },
        ids: [unit.UnitID],
        linkTo: 'adminPortalResidents'
    });

    await batch.commit();
    await Promise.all([
        incrementVersion('units_version'),
        incrementVersion('owners_version'),
        incrementVersion('vehicles_version')
    ]);
};

export const submitUserProfileUpdate = async (userAuthEmail: string, residentId: string, ownerId: string, newData: any): Promise<ProfileRequest> => {
    const batch = writeBatch(db);
    const reqRef = doc(collection(db, 'profileRequests'));
    const requestId = reqRef.id;

    const req: ProfileRequest = {
        id: requestId,
        residentId,
        ownerId,
        status: 'PENDING',
        changes: newData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
    };

    batch.set(reqRef, req);

    injectLogAndNotif(batch, {
        actionType: 'CREATE',
        module: 'Requests',
        description: `Yêu cầu cập nhật hồ sơ từ căn ${residentId}`,
        performedBy: { name: 'Resident', email: userAuthEmail },
        ids: [requestId],
        linkTo: 'adminPortalResidents'
    });

    await batch.commit();
    return req;
};

export const getAllPendingProfileRequests = async (): Promise<ProfileRequest[]> => {
    try {
        const q = query(collection(db, 'profileRequests'), where('status', '==', 'PENDING'));
        const snap = await getDocs(q);
        return snap.docs.map(d => ({ id: d.id, ...d.data() } as ProfileRequest));
    } catch (e) {
        return [];
    }
};

export const resolveProfileRequest = async (request: ProfileRequest, action: 'approve' | 'reject', adminEmail: string, approvedChanges?: any) => {
    const batch = writeBatch(db);
    const status = action === 'approve' ? 'APPROVED' : 'REJECTED';
    
    batch.update(doc(db, 'profileRequests', request.id), {
        status,
        updatedAt: new Date().toISOString(),
        resolvedBy: adminEmail
    });

    // Notify the resident
    const notifRef = doc(collection(db, 'notifications'));
    batch.set(notifRef, {
        userId: request.residentId,
        title: `Hồ sơ cá nhân: ${action === 'approve' ? 'ĐÃ DUYỆT' : 'BỊ TỪ CHỐI'}`,
        body: action === 'approve' ? 'Thông tin của bạn đã được cập nhật thành công.' : 'Yêu cầu cập nhật hồ sơ không được chấp nhận.',
        type: 'profile',
        link: 'portalProfile',
        isRead: false,
        createdAt: serverTimestamp()
    });

    // Log the action for admins
    injectLogAndNotif(batch, {
        actionType: 'UPDATE',
        module: 'Requests',
        description: `${action === 'approve' ? 'Duyệt' : 'Từ chối'} yêu cầu hồ sơ căn ${request.residentId}`,
        performedBy: { name: 'Admin', email: adminEmail },
        ids: [request.id],
        linkTo: 'adminPortalResidents'
    });

    return batch.commit();
};

export const updateResidentAvatar = async (ownerId: string, avatarUrl: string) => {
    return updateDoc(doc(db, 'owners', ownerId), { avatarUrl, updatedAt: new Date().toISOString() });
};

export const saveVehicles = async (
    vehicles: Vehicle[],
    actor?: { email: string, role: Role },
    reason?: string
) => {
    try {
        await chunkedBatchWrite(vehicles, (batch, v) => {
            const ref = doc(db, 'vehicles', v.VehicleId);
            batch.set(ref, v, { merge: true });
        }, async () => {
            if (actor && reason) {
                const logRef = doc(collection(db, 'activity_logs'));
                const summary = `Cập nhật ${vehicles.length} phương tiện: ${vehicles.map(v => v.PlateNumber).join(', ')}. Lý do: ${reason}`;
                await setDoc(logRef, {
                    actionType: 'UPDATE', 
                    module: 'Vehicles', 
                    description: summary, 
                    timestamp: serverTimestamp(), 
                    performedBy: { name: actor.role, email: actor.email }, 
                    ids: vehicles.map(v => v.VehicleId)
                });
            }
        });
        await incrementVersion('vehicles_version');
    } catch (e) {
        handleFirestoreError(e, OperationType.WRITE, 'vehicles');
    }
};

export const fetchOtherFeeConfigs = async (): Promise<OtherFeeConfig[]> => {
    return fetchCollectionWithOnSnapshot<OtherFeeConfig>('other_fee_configs');
};

export const saveOtherFeeConfig = async (config: OtherFeeConfig) => {
    try {
        await setDoc(doc(db, 'other_fee_configs', config.id), {
            ...config,
            updatedAt: new Date().toISOString()
        });
        await incrementVersion('other_fees_version');
    } catch (e) {
        handleFirestoreError(e, OperationType.WRITE, 'other_fee_configs');
    }
};

export const deleteOtherFeeConfig = async (id: string) => {
    try {
        await deleteDoc(doc(db, 'other_fee_configs', id));
        await incrementVersion('other_fees_version');
    } catch (e) {
        handleFirestoreError(e, OperationType.DELETE, 'other_fee_configs');
    }
};

export const fetchUserForLogin = async (identifier: string): Promise<UserPermission | null> => {
    try {
        const usersRef = collection(db, 'users');
        // First try direct get if identifier is email
        if (identifier.includes('@')) {
            const directSnap = await getDoc(doc(db, 'users', identifier));
            if (directSnap.exists()) return directSnap.data() as UserPermission;
        }

        const q1 = query(usersRef, where('Email', '==', identifier), limit(1));
        const q2 = query(usersRef, where('Username', '==', identifier), limit(1));
        const [snap1, snap2] = await Promise.all([getDocs(q1), getDocs(q2)]);
        const found = snap1.docs[0] || snap2.docs[0];
        return found ? (found.data() as UserPermission) : null;
    } catch (e) {
        handleFirestoreError(e, OperationType.LIST, 'users');
        return null;
    }
};

export const incrementVisitCount = async (isUser: boolean) => {
    const today = new Date().toISOString().split('T')[0];
    const docRef = doc(db, 'visit_stats', today);
    try {
        await setDoc(docRef, {
            id: today,
            date: today,
            guestCount: increment(isUser ? 0 : 1),
            userCount: increment(isUser ? 1 : 0),
            totalCount: increment(1)
        }, { merge: true });
    } catch (e) {
        // Silent fail for stats
        console.error('Failed to increment visit count', e);
    }
};

export const fetchVisitStats = async (): Promise<VisitStat[]> => {
    try {
        const q = query(collection(db, 'visit_stats'), orderBy('date', 'desc'), limit(30));
        const snap = await getDocs(q);
        return snap.docs.map(d => d.data() as VisitStat).reverse();
    } catch (e) {
        handleFirestoreError(e, OperationType.LIST, 'visit_stats');
        return [];
    }
};

/**
 * Link Firebase Auth UID to user document for security rules
 */
export const linkUserUid = async (email: string, uid: string) => {
    try {
        const batch = writeBatch(db);
        const userRef = doc(db, 'users', email);
        const mappingRef = doc(db, 'uid_mapping', uid);
        
        batch.update(userRef, { uid });
        batch.set(mappingRef, { email });
        
        await batch.commit();
    } catch (e) {
        handleFirestoreError(e, OperationType.UPDATE, `users/${email}`);
    }
};

export const deleteWaterReadingsForPeriod = async (period: string, unitIds: string[]) => {
    try {
        await chunkedBatchWrite(unitIds, (batch, unitId) => {
            const id = `${period}_${unitId}`;
            batch.delete(doc(db, 'waterReadings', id));
        });
        await del(`water_${period}`);
    } catch (e) {
        handleFirestoreError(e, OperationType.DELETE, 'waterReadings');
    }
};

export const saveWaterReadings = async (readings: WaterReading[]) => {
    try {
        await chunkedBatchWrite(readings, (batch, r) => {
            const id = `${r.Period}_${r.UnitID}`;
            const ref = doc(db, 'waterReadings', id);
            batch.set(ref, r);
        });
        // Clear water cache for these periods
        const periods = Array.from(new Set(readings.map(r => r.Period)));
        for (const p of periods) {
            await del(`water_${p}`);
        }
    } catch (e) {
        handleFirestoreError(e, OperationType.WRITE, 'waterReadings');
    }
};

// --- NEW FUNCTIONS FOR PRODUCTION ---

export const saveTariffs = async (newTariffs: TariffCollection) => {
    settingsCache.tariffs = newTariffs;
    await setDoc(doc(db, 'settings', 'tariffs'), newTariffs);
    await incrementVersion('tariffs_version');
};

export const saveAdjustments = async (newAdjustments: Adjustment[]) => {
    try {
        await chunkedBatchWrite(newAdjustments, (batch, adj) => {
            const id = `${adj.Period}_${adj.UnitID}_${Math.abs(adj.Amount)}`; 
            const ref = doc(db, 'adjustments', id);
            batch.set(ref, adj);
        });
    } catch (e) {
        handleFirestoreError(e, OperationType.WRITE, 'adjustments');
    }
};

export const updateChargeStatuses = async (period: string, unitIds: string[], updates: { isPrinted?: boolean; isSent?: boolean }) => {
    const batch = writeBatch(db);
    unitIds.forEach(unitId => {
        const id = `${period}_${unitId}`;
        batch.update(doc(db, 'charges', id), updates);
    });
    await batch.commit();
    await invalidateCache(`charges_${period}`);
};

export const updateChargePayments = async (period: string, paymentUpdates: Map<string, number>) => {
    const batch = writeBatch(db);
    paymentUpdates.forEach((amount, unitId) => {
        const id = `${period}_${unitId}`;
        batch.update(doc(db, 'charges', id), {
            TotalPaid: amount,
            paymentStatus: 'paid_ck',
            PaymentConfirmed: true,
            updatedAt: serverTimestamp()
        });
    });
    await batch.commit();
    await invalidateCache(`charges_${period}`);
};

export const updatePaymentStatusBatch = async (period: string, unitIds: string[], newStatus: 'paid' | 'unpaid') => {
    const batch = writeBatch(db);
    unitIds.forEach(unitId => {
        const id = `${period}_${unitId}`;
        batch.update(doc(db, 'charges', id), {
            paymentStatus: newStatus,
            PaymentConfirmed: newStatus === 'paid',
            updatedAt: serverTimestamp()
        });
    });
    await batch.commit();
    await invalidateCache(`charges_${period}`);
};

export const importResidentsBatch = async (
    _u: Unit[], _o: Owner[], _v: Vehicle[], updates: any[]
) => {
    // Perform batched writes for imported data
    const batch = writeBatch(db);
    
    updates.forEach(row => {
        if (row.unitId) {
            // Update/Create Unit
            batch.set(doc(db, 'units', row.unitId), {
                UnitID: row.unitId,
                OwnerID: row.unitId, // Simple mapping: UnitID = OwnerID key
                UnitType: row.unitType,
                Area_m2: row.area,
                Status: row.status
            }, { merge: true });

            // Update/Create Owner
            batch.set(doc(db, 'owners', row.unitId), {
                OwnerID: row.unitId,
                OwnerName: row.ownerName,
                Phone: row.phone,
                Email: row.email,
                updatedAt: new Date().toISOString()
            }, { merge: true });

            // Update/Create Vehicles
            if (row.vehicles && Array.isArray(row.vehicles)) {
                row.vehicles.forEach((v: any) => {
                    // Generate a deterministic ID if possible, or random
                    const vId = v.PlateNumber ? v.PlateNumber.replace(/[^a-zA-Z0-9]/g, '') : `VEH_${Date.now()}_${Math.random()}`;
                    batch.set(doc(db, 'vehicles', vId), {
                        VehicleId: vId,
                        UnitID: row.unitId,
                        PlateNumber: v.PlateNumber,
                        Type: v.Type,
                        isActive: true,
                        StartDate: new Date().toISOString(),
                        updatedAt: new Date().toISOString()
                    }, { merge: true });
                });
            }
        }
    });

    await batch.commit();
    return { createdCount: updates.length, updatedCount: 0 };
};

export const resetUserPassword = async (email: string) => {
    // Firebase Client SDK method
    await sendPasswordResetEmail(auth, email);
};

export const createAdminNotification = async (notif: Partial<AdminNotification>) => {
    try {
        const ref = collection(db, 'adminNotifications');
        await addDoc(ref, {
            ...notif,
            isRead: false,
            createdAt: serverTimestamp()
        });
    } catch (e) {
        handleFirestoreError(e, OperationType.WRITE, 'adminNotifications');
    }
};

export const resetUserPasswordToDefault = async (email: string, role: Role) => {
    try {
        const defaultPassword = role === 'Resident' ? '123456a' : '123456a@';
        const ref = doc(db, 'users', email);
        await updateDoc(ref, {
            password: defaultPassword,
            mustChangePassword: true,
            updatedAt: serverTimestamp()
        });
        await invalidateCache('users');
    } catch (e) {
        handleFirestoreError(e, OperationType.UPDATE, `users/${email}`);
    }
};

export const wipeAllBusinessData = async (progressCallback: (msg: string) => void) => {
    progressCallback("Starting wipe...");
    const collections = ['charges', 'waterReadings', 'activity_logs', 'notifications', 'operational_expenses', 'misc_revenues', 'profileRequests'];
    
    for (const col of collections) {
        progressCallback(`Deleting collection: ${col}...`);
        const q = query(collection(db, col), limit(500));
        const snap = await getDocs(q);
        const batch = writeBatch(db);
        snap.docs.forEach(d => batch.delete(d.ref));
        await batch.commit();
    }
    
    // Note: Deleting large collections in client SDK is not recommended for production massive data. 
    // This is a "soft" wipe for demo/small scale.
    progressCallback("Wipe complete.");
};

export const getPendingProfileRequest = async (residentId: string): Promise<ProfileRequest | null> => {
    try {
        const q = query(
            collection(db, 'profileRequests'), 
            where('residentId', '==', residentId), 
            where('status', '==', 'PENDING'), 
            limit(1)
        );
        const snap = await getDocs(q);
        return !snap.empty ? (snap.docs[0].data() as ProfileRequest) : null;
    } catch {
        return null;
    }
};
